import React, { useState, useEffect } from 'react'
import { useLanguage } from '../LanguageContext'
import { getBlogs } from '../api'

// ─────────────────────────────────────────────────────────────────────────────
//  Blog list page
// ─────────────────────────────────────────────────────────────────────────────

export default function Blog({ onBack }) {
  const { t } = useLanguage()
  const [posts, setPosts]           = useState([])
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState(null)
  const [activePost, setActivePost] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    getBlogs()
      .then(data => {
        if (!cancelled) {
          setPosts(data.blogs || [])
          setLoading(false)
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err.message || 'Failed to load articles.')
          setLoading(false)
        }
      })

    return () => { cancelled = true }
  }, [])

  // ── Show individual article ──────────────────────────────────────────────
  if (activePost) {
    return (
      <BlogPost
        post={activePost}
        onBack={() => setActivePost(null)}
        t={t}
      />
    )
  }

  // ── Loading state ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="db-page cnt-page cnt-page--wide">
        <BlogHeader onBack={onBack} t={t} />
        <div className="cnt-blog-loading">
          <span className="cnt-blog-loading__dot" />
          <span className="cnt-blog-loading__dot" />
          <span className="cnt-blog-loading__dot" />
        </div>
      </div>
    )
  }

  // ── Error state ──────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="db-page cnt-page cnt-page--wide">
        <BlogHeader onBack={onBack} t={t} />
        <p className="cnt-blog-error">{error}</p>
      </div>
    )
  }

  // ── Empty state ──────────────────────────────────────────────────────────
  if (posts.length === 0) {
    return (
      <div className="db-page cnt-page cnt-page--wide">
        <BlogHeader onBack={onBack} t={t} />
        <p className="cnt-blog-empty">{t('blogEmpty') || 'No articles published yet.'}</p>
      </div>
    )
  }

  return (
    <div className="db-page cnt-page cnt-page--wide">
      <BlogHeader onBack={onBack} t={t} />

      <div className="cnt-blog-list">
        {posts.map((post, i) => (
          <article
            key={post.id}
            className="cnt-blog-row"
            onClick={() => setActivePost(post)}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && setActivePost(post)}
            aria-label={post.title}
            style={{ animationDelay: `${i * 70}ms` }}
          >
            {post.image_url && (
              <div className="cnt-blog-row__img-wrap">
                <img
                  src={post.image_url}
                  alt={post.title}
                  className="cnt-blog-row__img"
                  loading="lazy"
                />
              </div>
            )}
            <div className="cnt-blog-row__right">
              <div className="cnt-blog-row__meta">
                <span className="cnt-blog-date">{post.date}</span>
              </div>
              <h3 className="cnt-blog-row__title">{post.title}</h3>
              <p className="cnt-blog-row__excerpt">{post.short_content}</p>
              <span className="cnt-blog-read-more cnt-blog-read-more--sm">
                {t('readMore') || 'Read article →'}
              </span>
            </div>
          </article>
        ))}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
//  Shared page header
// ─────────────────────────────────────────────────────────────────────────────

function BlogHeader({ onBack, t }) {
  return (
    <div className="cnt-header">
      <button className="cnt-back" onClick={onBack} aria-label={t('back')}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        {t('dashboard')}
      </button>
      <div className="cnt-header__title-wrap">
        <p className="cnt-header__overline">{t('aboutOverline')}</p>
        <h1 className="cnt-header__title">{t('journal')}</h1>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
//  Single article view
// ─────────────────────────────────────────────────────────────────────────────

function BlogPost({ post, onBack, t }) {
  return (
    <div className="db-page cnt-page cnt-page--article">

      <div className="cnt-header">
        <button className="cnt-back" onClick={onBack} aria-label={t('backToJournal')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {t('journal')}
        </button>
      </div>

      <div className="cnt-article">

        {post.image_url && (
          <div className="cnt-article__hero-wrap">
            <img
              src={post.image_url}
              alt={post.title}
              className="cnt-article__hero"
              loading="lazy"
            />
          </div>
        )}

        <div className="cnt-article__meta">
          <span className="cnt-blog-date">{post.date}</span>
        </div>

        <h1 className="cnt-article__title">{post.title}</h1>
        <p className="cnt-article__excerpt">{post.short_content}</p>

        <div className="cnt-article__rule" aria-hidden="true" />

        {/*
          content comes from TinyMCE and is stored as HTML.
          dangerouslySetInnerHTML is intentional here — the content is
          admin-authored only, never user-submitted, so XSS is not a concern.
        */}
        <div
          className="cnt-article__body"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        <div className="cnt-article__sig">{t('signatureMwuilu')}</div>

      </div>
    </div>
  )
}